import { useEffect, useState } from 'react'
import { useDarkModeAvailable } from 'app/hooks/useDarkModeAvailable'
import { useThemeReady } from 'app/hooks/useThemeReady'
import { useUserSettingStore } from 'app/stores/StoreContext'
import { bg } from 'lib/background'
import { logWarn } from 'lib/logging'
import { APPEARANCE_THEME_SETTING, THEME_CACHE_KEY, parseThemePreference, resolveTheme } from 'lib/theme'

const PREFERS_DARK = `(prefers-color-scheme: dark)`

/**
 * Mirrors the effective theme onto <html>. Call once from the app shell, which must be an `observer()`.
 */
export function useThemeSync(): void {
  const settingStore = useUserSettingStore()
  const toggleEnabled = useDarkModeAvailable()
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => window.matchMedia(PREFERS_DARK).matches)
  // Until ready, index.html's cached class and the boot cache stay, so a dark user never flashes light.
  const ready = useThemeReady()
  const theme = resolveTheme({
    toggleEnabled,
    preference: parseThemePreference(settingStore.settings[APPEARANCE_THEME_SETTING]),
    systemPrefersDark,
  })

  useEffect(() => {
    bg(async () => settingStore.get(APPEARANCE_THEME_SETTING))
  }, [settingStore])

  useEffect(() => {
    const media = window.matchMedia(PREFERS_DARK)
    const onChange = (event: MediaQueryListEvent) => setSystemPrefersDark(event.matches)
    media.addEventListener(`change`, onChange)
    return () => media.removeEventListener(`change`, onChange)
  }, [])

  useEffect(() => {
    const root = document.documentElement
    if (!ready) {
      // StrictMode's rehearsal unmount runs the cleanup below before the stores settle, so
      // reapply index.html's boot class rather than trust it survived.
      try {
        if (localStorage.getItem(THEME_CACHE_KEY) !== `dark`) return
      } catch (err) {
        logWarn(`Theme cache unavailable`, err)
        return
      }
      root.classList.add(`dark`)
      root.style.colorScheme = `dark`
      return
    }
    root.classList.toggle(`dark`, theme === `dark`)
    root.style.colorScheme = theme
    // A boot cache for index.html only; the preference itself lives in the user setting.
    try {
      if (toggleEnabled) localStorage.setItem(THEME_CACHE_KEY, theme)
      else localStorage.removeItem(THEME_CACHE_KEY)
    } catch (err) {
      logWarn(`Theme cache unavailable`, err)
    }
  }, [ready, theme, toggleEnabled])

  useEffect(
    () => () => {
      document.documentElement.classList.remove(`dark`)
      document.documentElement.style.colorScheme = ``
    },
    [],
  )
}
